/**
 * A named variable the estimator authored on an estimate or a section, expanded from the compact
 * JSON the editor stores (`n`, `v`, `u`, `c`). Mirrors `EstimateVariable` in ProjulV1Portal.
 */
export interface EstimateVariable {
  Name: string;
  Value: string;
  Unit?: string;
  IsViewable?: boolean;
}

/** A client-visible variable reduced to what the line renders; the unit rides inside the value. */
export interface ClientVisibleVariable {
  Name: string;
  Value: string;
}

function asText(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

/**
 * The one printable-value rule this app shares with the portal's print tree and the backend's
 * redaction: the trimmed text parses as a finite number. Nothing here resolves a formula.
 */
export function isPlainNumericValue(value: string | number | null | undefined): boolean {
  const text = asText(value);
  // Number('') is 0, so a blank has to be refused before the parse.
  if (text === '') return false;
  return Number.isFinite(Number(text));
}

/** How a caller hears that a stored column could not be read, so the degrade to "no variables" is reported. */
export type UnreadableVariablesReporter = (stored: string, error: unknown) => void;

/** Accepts the compact keys and the expanded names, because the two apps deploy independently. */
export function parseEstimateVariables(
  variables: string | null | undefined,
  onUnreadable?: UnreadableVariablesReporter
): EstimateVariable[] {
  if (!variables) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(variables);
  } catch (error) {
    // The customer still gets their estimate; the caller hears why this scope shows no variables.
    onUnreadable?.(variables, error);
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed
    .filter(entry => !!entry && typeof entry === 'object')
    .map(entry => ({
      Name: asText(entry.Name ?? entry.n),
      Value: asText(entry.Value ?? entry.v),
      Unit: asText(entry.Unit ?? entry.u),
      IsViewable: (entry.IsViewable ?? entry.c) === true,
    }));
}

/**
 * The render list for one scope, in authored order. Re-applies the client-visible filter the server
 * already ran and fails closed on a formula value; no name is special, so `units` renders like any row.
 */
export function getClientVisibleVariables(variables: EstimateVariable[]): ClientVisibleVariable[] {
  return variables
    .filter(
      variable =>
        !!variable &&
        variable.IsViewable === true &&
        asText(variable.Name).length > 0 &&
        isPlainNumericValue(variable.Value)
    )
    .map(variable => ({
      Name: asText(variable.Name),
      Value: [asText(variable.Value), asText(variable.Unit)].filter(part => part.length > 0).join(' '),
    }));
}
